import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { isEmpty, find, get, some, map, filter, throttle } from 'lodash';
import styled from 'styled-components/macro';
import Platform from 'platform';
import i18n from 'i18n';
import keys from 'config/keys';
import scaleSize from 'utils/scale-size';
import PLAYER_TYPES from 'constants/player-types';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import withKeepingLastFocus from 'components/HOCs/withKeepingLastFocus';
import { hideEPG } from 'actions/epg-widget';
import { fetchEpg, fetchExtraEPGItems } from 'actions/channels';
import { navigateToPlayerPage, resolveTimeShiftPlayer } from 'actions/player';
import { navigateToAuthPage } from 'actions/app';
import { getIsLoggedInSelector } from 'reducers/profile/selectors';
import { selectIsParentalControlEnabled } from 'reducers/parental-lock/selectors';
import useButtonClick from 'hooks/useButtonClick';
import useModals from 'hooks/useModals';
import useLeaveFor from 'hooks/useLeaveFor';
import BackButton from 'components/BackButton';
import ArrowIcon from 'components/Icons/back';
import CategoriesList from './CategoriesList';
import ChannelsList from './ChannelsList';
import ProgramsList from './ProgramsList';
import ProgramInfo from './ProgramInfo';
import EmptyStub from './EmptyStub';
import EPGLoader from './EPGLoader';
import getLocalized from 'utils/get-localized';
import { resolvePlayerNavLink } from 'helpers/urlhelper';
import NotLoggedStub from './NotLoggedStub';
import useOnNavigationBack from 'hooks/useOnNavigationBack';

const LEFT_LIST_WIDTH = 840;
const RIGHT_LIST_WIDTH = 880;

const LAST_FOCUS_KEY = 'EPG';

const CATEGORIES_SECTION_NAME = 'CATEGORIES_LIST';
const CHANNELS_SECTION_NAME = 'CHANNELS_LIST';
const PROGRAMS_SECTION_NAME = 'PROGRAMS_LIST';

const EPG_REQUEST_TIMEOUT = 1000;

const LIST_POSITIONS = {
  CATEGORIES: 0,
  CHANNELS: 1,
  PROGRAMS: 2,
};

const onWheelHandler = throttle((deltaY) => {
  const direction = deltaY > 0 ? 'down' : 'up';
  SpatialNavigationWrapper.move(direction);
}, 300);

const StyledEPGWrapper = styled.div`
  position: absolute;
  z-index: 1;
  height: ${scaleSize(1080)}px;
  width: ${scaleSize(1920)}px;
  background-color: rgba(0, 0, 0, 0.8);
  color: ${({ theme }) => theme.defaultColor};
`;

const StyledListsWrapper = styled.div`
  position: relative;
  left: ${({ listPosition }) => {
    if (listPosition === LIST_POSITIONS.CATEGORIES) return scaleSize(115);
    if (listPosition === LIST_POSITIONS.CHANNELS) return scaleSize(-795);
    if (listPosition === LIST_POSITIONS.PROGRAMS) return scaleSize(-1705);
  }}px;
  top: ${scaleSize(70)}px;
  white-space: nowrap;
`;

const StyledBackButton = styled(BackButton)`
  box-sizing: border-box;
  height: ${scaleSize(1080)}px;
  width: ${scaleSize(100)}px;
  left: 0;
  top: 0;
  padding-top: ${scaleSize(29)}px;
  padding-left: ${scaleSize(34)}px;

  &:focus {
    background: ${({ theme }) => theme.activeColor};
    svg > path {
      stroke: ${({ theme }) => theme.defaultColor};
    }
  }
`;

const StyledCategoriesPointer = styled.div`
  display: block;
  position: absolute;
  width: ${scaleSize(142)}px;
  height: ${scaleSize(48)}px;
  left: -${scaleSize(25)}px;
  top: ${scaleSize(515)}px;
  font-family: RobotoMedium, sans-sefir;
  font-style: normal;
  font-weight: 900;
  text-transform: uppercase;
  text-align: center;
  font-size: ${scaleSize(22)}px;
  line-height: ${scaleSize(48)}px;
  letter-spacing: 2px;
  color: #ffffff;
  transform: rotate(-90deg);

  svg {
    height: ${scaleSize(28)}px;
    width: ${scaleSize(22)}px;
    display: block;
    margin: 0 auto;
    transform: rotate(90deg);
  }
`;

const EPGWidget = ({
  channels,
  categories,
  currentActiveCategoryId,
  currentPlayingChannelId,
  currentPlayingProgramId,
  isLifecellUser,
  lastFocusKeeper,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { showEnterPinModal, showLifecellPaymentModal } = useModals();
  const channelFocusTimeoutRef = useRef(null);

  const lastFocus = lastFocusKeeper.getSaved(LAST_FOCUS_KEY) || {};
  // state
  const [listPosition, setListPosition] = useState(LIST_POSITIONS.CHANNELS);
  const [activeCategoryId, setActiveCategoryId] = useState(() => {
    const isActiveCategoryExists = some(categories, { id: currentActiveCategoryId });
    return isActiveCategoryExists ? currentActiveCategoryId : categories[0].id;
  });
  const [focusedChannelId, setFocusedChannelId] = useState(currentPlayingChannelId || lastFocus.channelId);
  const [focusedProgramId, setFocusedProgramId] = useState(currentPlayingProgramId);
  // selectors
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const isParentalControlEnabled = useSelector(selectIsParentalControlEnabled);
  // computed values
  const activeCategory = find(categories, { id: activeCategoryId });
  const activeCategoryChannels = useMemo(() => {
    const matched = map(activeCategory.channelIds, (id) => find(channels, { id }));

    return filter(matched, (c) => !!c);
  }, [activeCategory, channels]);
  const isActiveCategoryHasChannels = !isEmpty(activeCategoryChannels);
  const focusedChannel = find(activeCategoryChannels, { id: focusedChannelId });
  const isActiveChannelHasPrograms = !isEmpty(get(focusedChannel, ['epg']));
  const isProgramsOnLoading = isLoggedIn && focusedChannel && (!focusedChannel.epg || focusedChannel.isOnLazyload);
  const backButtonKeyCode = Platform.getKeyCode(keys.BACK);

  // handlers
  const hideEpgAction = useCallback(() => {
    const { pathname } = history.location;
    const isPlayerPage = pathname.includes('player');
    dispatch(hideEPG());
    // fix state error if EPG was closed
    clearTimeout(channelFocusTimeoutRef.current);
    // If the player is not playing the channel, then
    // need to exit the player when the EPG closes.
    // Important: need to perform only one "goBack"
    // because "hideEpgAction" performs also on location
    // change for some cases. Check "useLeaveFor: hook call
    if ((!currentPlayingChannelId || !isLoggedIn) && isPlayerPage) {
      history.goBack();
    }
  }, [currentPlayingChannelId, dispatch, history, isLoggedIn]);

  const onCategoriesFocus = useCallback(() => setListPosition(LIST_POSITIONS.CATEGORIES), []);

  const onCategoryFocus = useCallback(
    (item) => {
      const { id } = item;
      if (id === activeCategoryId) return;

      setActiveCategoryId(id);
      // reset active channel
      setFocusedChannelId(null);
    },
    [activeCategoryId],
  );

  const onCategoryClick = useCallback(() => {
    if (isActiveCategoryHasChannels) {
      SpatialNavigationWrapper.focus(`@${CHANNELS_SECTION_NAME}`);
    }
  }, [isActiveCategoryHasChannels]);

  const onChannelClick = useCallback(
    async (item) => {
      if (!get(item, ['id'])) return;

      const isDVR = !!item.tvServices.catchupTv;
      const query = { activeCategoryId };
      const focusBackConfig = { sectionToFocus: CHANNELS_SECTION_NAME };

      if (isDVR && item?.currentProgram?.startTime) {
        Object.assign(query, { timeshiftBegin: item?.currentProgram?.startTime });
      }

      const goToPlayer = () => {
        dispatch(navigateToPlayerPage({ item, type: PLAYER_TYPES.CHANNEL, useReplace: true, query }));
        // hack for right focus after modal close (success case)
        SpatialNavigationWrapper.focus(`@${CHANNELS_SECTION_NAME}`);
      };

      const timeshiftBegin = await dispatch(resolveTimeShiftPlayer(item));

      const onSuccess = resolvePlayerNavLink(item, {
        type: PLAYER_TYPES.CHANNEL,
        isVodChannel: !!item.isVod,
        timeshiftBegin,
        query,
      });

      const search = `?channelId=${item.id}&onSuccess=${encodeURIComponent(onSuccess)}`;

      if (!isLoggedIn) {
        dispatch(navigateToAuthPage(search));
        return;
      }

      if (item.notAvailable) {
        if (isLifecellUser) {
          showLifecellPaymentModal({ id: item.id, assetType: 'channelId', onSuccess: goToPlayer }, focusBackConfig);
          return;
        }
        // https://hootko.atlassian.net/browse/STVWL-602
        lastFocusKeeper.save(LAST_FOCUS_KEY, { channelId: item.id });
        return history.push({
          pathname: '/profile/packages/filter',
          search,
        });
      }

      if (isParentalControlEnabled && item.isAdult) {
        return showEnterPinModal(focusBackConfig, goToPlayer);
      }

      if (item.id === currentPlayingChannelId) {
        return hideEpgAction();
      }

      goToPlayer();
      hideEpgAction();
    },
    [
      activeCategoryId,
      dispatch,
      isLoggedIn,
      isParentalControlEnabled,
      currentPlayingChannelId,
      hideEpgAction,
      isLifecellUser,
      lastFocusKeeper,
      history,
      showLifecellPaymentModal,
      showEnterPinModal,
    ],
  );

  const onChannelsListFocus = () => setListPosition(LIST_POSITIONS.CHANNELS);

  const onChannelFocus = useCallback(
    (item) => {
      const { id } = item;
      if (id === focusedChannelId) return;
      clearTimeout(channelFocusTimeoutRef.current);
      // setting setFocusedChannelId runs EPG loading
      channelFocusTimeoutRef.current = setTimeout(() => setFocusedChannelId(id), EPG_REQUEST_TIMEOUT);

      setFocusedChannelId(null);
    },
    [focusedChannelId],
  );

  const onProgramFocus = useCallback(
    (_item, focusProgramIndex) => {
      const { length: size } = focusedChannel.epg;
      const threshold = 5;

      let direction;

      if (focusProgramIndex + threshold >= size) {
        direction = 'next';
      } else if (focusProgramIndex <= threshold) {
        direction = 'prev';
      }

      if (direction && !focusedChannel.isVod) dispatch(fetchExtraEPGItems({ channelId: focusedChannel.id, direction }));
      setFocusedProgramId(_item.id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [focusedChannel],
  );
  const onProgramClick = useCallback(
    (item) => {
      const { channelId } = item;
      const type = PLAYER_TYPES.CHANNEL;
      const currentChannel = activeCategoryChannels.find((channel) => channel.id === channelId);
      const focusBackConfig = { sectionToFocus: PROGRAMS_SECTION_NAME };
      const query = { activeCategoryId };
      const isLive = item.id === get(currentChannel.currentProgram, ['id']);

      const goToPlayer = () => {
        if (isLive && !currentChannel.isVod) {
          const isDVR = !!currentChannel.tvServices?.catchupTv;

          if (isDVR && item.startTime) {
            Object.assign(query, { timeshiftBegin: item.startTime });
          }

          dispatch(navigateToPlayerPage({ item: { id: channelId }, type, query, useReplace: true }));
        } else if (['CATCHUP', 'LIVE_CATCHUP'].includes(item.state)) {
          const metadata = { startTime: item.startTime };
          dispatch(
            navigateToPlayerPage({
              item: { id: channelId, metadata },
              type,
              query,
              useReplace: true,
            }),
          );
        }
      };

      const metadata = ['CATCHUP', 'LIVE_CATCHUP'].includes(item.state) ? { startTime: item.startTime } : null;
      const onSuccess = resolvePlayerNavLink(
        { id: channelId, metadata },
        { type: PLAYER_TYPES.CHANNEL, isVodChannel: !!currentChannel.isVod, query },
      );
      const search = `?channelId=${currentChannel.id}&onSuccess=${encodeURIComponent(onSuccess)}`;

      if (!isLoggedIn) {
        dispatch(navigateToAuthPage(search));
        return;
      }

      if (currentChannel.notAvailable) {
        if (isLifecellUser) {
          showLifecellPaymentModal(
            {
              id: currentChannel.channelId,
              assetType: 'channelId',
              onSuccess: goToPlayer,
            },
            focusBackConfig,
          );

          return;
        }
        // https://hootko.atlassian.net/browse/STVWL-602
        lastFocusKeeper.save(LAST_FOCUS_KEY, { channelId });
        return history.push({
          pathname: '/profile/packages/filter',
          search,
        });
      }
      if (isParentalControlEnabled && currentChannel.isAdult) {
        return showEnterPinModal(focusBackConfig, goToPlayer);
      }

      goToPlayer();
      hideEpgAction();
    },
    [
      activeCategoryChannels,
      activeCategoryId,
      isLoggedIn,
      isParentalControlEnabled,
      hideEpgAction,
      dispatch,
      isLifecellUser,
      lastFocusKeeper,
      history,
      showLifecellPaymentModal,
      showEnterPinModal,
    ],
  );

  useButtonClick(backButtonKeyCode, hideEpgAction);

  useLeaveFor(history, ({ pathname }) => {
    // if it's navigation inside player - do nothing
    if (pathname.includes('player')) return;
    // Close EPG if user navigate outside the player
    // e.g. click login, buy subs etc
    hideEpgAction();
  });

  useOnNavigationBack(() => lastFocusKeeper.reset(LAST_FOCUS_KEY));

  useEffect(() => {
    if (focusedChannel && !focusedChannel.epg && isLoggedIn) dispatch(fetchEpg({ channelId: focusedChannel.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedChannel]);

  useEffect(() => {
    // set initial focus on channels after first render
    setTimeout(() => SpatialNavigationWrapper.focus(`@${CHANNELS_SECTION_NAME}`), 0);
  }, []);

  const programs = useMemo(() => {
    if (!isLoggedIn && listPosition !== LIST_POSITIONS.CATEGORIES) return <NotLoggedStub />;

    if (isActiveChannelHasPrograms) {
      return (
        <>
          <ProgramsList
            sectionName={PROGRAMS_SECTION_NAME}
            focusedChannel={focusedChannel}
            currentPlayingProgramId={currentPlayingProgramId}
            currentPlayingChannelId={currentPlayingChannelId}
            onEnter={onProgramClick}
            onFocus={onProgramFocus}
            onListFocus={() => setListPosition(LIST_POSITIONS.PROGRAMS)}
          />
          <ProgramInfo
            channel={focusedChannel}
            program={find(focusedChannel.epg, { id: focusedProgramId })}
          ></ProgramInfo>
        </>
      );
    }

    const needToShowStub = !isProgramsOnLoading && focusedChannel && !focusedChannel.isOnLazyload;

    if (needToShowStub) return <EmptyStub type="programs" />;

    return null;
  }, [
    isLoggedIn,
    listPosition,
    isActiveChannelHasPrograms,
    isProgramsOnLoading,
    focusedChannel,
    currentPlayingProgramId,
    currentPlayingChannelId,
    onProgramClick,
    onProgramFocus,
    focusedProgramId,
  ]);

  return (
    <StyledEPGWrapper onWheel={(e) => onWheelHandler(e.deltaY)}>
      {listPosition === LIST_POSITIONS.CATEGORIES && <StyledBackButton onEnter={hideEpgAction} />}
      {[LIST_POSITIONS.CHANNELS, LIST_POSITIONS.PROGRAMS].includes(listPosition) && (
        <StyledCategoriesPointer>
          <ArrowIcon />
          {listPosition === LIST_POSITIONS.CHANNELS ? i18n.t('epg.categories.title') : i18n.t('epg.channelTitle')}
        </StyledCategoriesPointer>
      )}
      <StyledListsWrapper listPosition={listPosition}>
        <CategoriesList
          sectionName={CATEGORIES_SECTION_NAME}
          isVisible={listPosition === LIST_POSITIONS.CATEGORIES}
          activeCategoryId={activeCategoryId}
          content={categories}
          onFocus={onCategoryFocus}
          onEnter={onCategoryClick}
          onListFocus={onCategoriesFocus}
        />
        {isActiveCategoryHasChannels ? (
          <ChannelsList
            sectionName={CHANNELS_SECTION_NAME}
            title={getLocalized(activeCategory.names, activeCategory.name)}
            content={activeCategoryChannels}
            isVisible={listPosition !== LIST_POSITIONS.PROGRAMS}
            width={listPosition === LIST_POSITIONS.CHANNELS ? LEFT_LIST_WIDTH : RIGHT_LIST_WIDTH}
            onEnter={onChannelClick}
            onFocus={onChannelFocus}
            onListFocus={onChannelsListFocus}
            renderSize={5}
            currentPlayingChannelId={currentPlayingChannelId}
            focusedChannelId={focusedChannelId}
            key={activeCategoryId}
          />
        ) : (
          <EmptyStub type="channels" />
        )}
        {programs}
        {isProgramsOnLoading && <EPGLoader />}
      </StyledListsWrapper>
    </StyledEPGWrapper>
  );
};

export default withKeepingLastFocus(EPGWidget);
